// frontend/src/components/tax/CrossVerifyPanel.tsx
//
// TAX-PARSE-2 — runs / displays the 3-year tax cross-verification.
// Flag coloring mirrors BankCorroborationCard.tsx (PASS/REVIEW/FLAG palette).

import { useState } from 'react'
import { RefreshCw } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table'
import { formatApiError } from '@/utils/httpErrors'
import { crossVerifyTax, type CrossVerificationResult, type CrossVerifyFlag } from '@/api/tax'

const FLAG_STYLES: Record<CrossVerifyFlag, string> = {
  PASS: 'bg-green-50 text-green-800 border-green-200',
  REVIEW: 'bg-amber-50 text-amber-800 border-amber-200',
  FLAG: 'bg-red-50 text-red-800 border-red-200',
}

const FLAG_CELL_STYLES: Record<string, string> = {
  PASS: 'bg-green-50/60',
  REVIEW: 'bg-amber-50/60',
  FLAG: 'bg-red-50/60',
}

function FlagBadge({ flag }: { flag: CrossVerifyFlag | string }) {
  const styles = FLAG_STYLES[flag as CrossVerifyFlag] ?? 'bg-muted text-muted-foreground border-border'
  return (
    <span
      className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-semibold tracking-wide ${styles}`}
    >
      {flag}
    </span>
  )
}

function fmtUsd(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n)
}

function fmtPct(n: number): string {
  return `${n.toFixed(1)}%`
}

interface CrossVerifyPanelProps {
  dealId: string
  docIds: string[]
  onAddDiscrepancyToMir?: (discrepancy: CrossVerificationResult['discrepancies'][number]) => void
}

export function CrossVerifyPanel({ dealId, docIds, onAddDiscrepancyToMir }: CrossVerifyPanelProps) {
  const [result, setResult] = useState<CrossVerificationResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const runVerify = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await crossVerifyTax(dealId, docIds.length > 0 ? docIds : undefined)
      setResult(res)
    } catch (err) {
      setError(formatApiError(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={runVerify} disabled={loading || !dealId}>
          <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Running cross-verification…' : 'Run Cross-Verification'}
        </Button>
        {result && <FlagBadge flag={result.overall_flag} />}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {!error && !result && !loading && (
        <p className="text-sm text-muted-foreground">
          Run cross-verification to compare tax-return figures against bank-derived and P&amp;L
          figures across the years on file for this deal.
        </p>
      )}

      {result && (
        <>
          {/* Summary narrative */}
          {result.summary && (
            <div className="rounded-lg border bg-card p-3">
              <p className="text-sm text-foreground/80">{result.summary}</p>
            </div>
          )}

          {/* Proof of cash */}
          <div className="rounded-lg border bg-card p-4 space-y-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Proof of Cash — FY{result.proof_of_cash.year}
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <p className="text-[11px] text-muted-foreground">Tax-Return Revenue</p>
                <p className="text-lg font-semibold tabular-nums">
                  {fmtUsd(result.proof_of_cash.tax_revenue)}
                </p>
              </div>
              <div>
                <p className="text-[11px] text-muted-foreground">Bank-Normalized Deposits</p>
                <p className="text-lg font-semibold tabular-nums">
                  {fmtUsd(result.proof_of_cash.bank_normalized_deposits)}
                </p>
              </div>
              <div>
                <p className="text-[11px] text-muted-foreground">Variance</p>
                <p className="text-lg font-semibold tabular-nums">
                  {fmtUsd(result.proof_of_cash.variance_abs)} ({fmtPct(result.proof_of_cash.variance_pct)})
                </p>
              </div>
            </div>
            <FlagBadge flag={result.proof_of_cash.flag} />
          </div>

          {/* Year-over-year rows */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Year</TableHead>
                  <TableHead>Revenue Check</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {result.rows.map((row) => (
                  <TableRow key={row.year} className={FLAG_CELL_STYLES[row.overall_flag] ?? ''}>
                    <TableCell className="font-medium">FY{row.year}</TableCell>
                    <TableCell className="whitespace-normal text-xs text-muted-foreground">
                      {Object.entries(row.revenue_check).length === 0
                        ? '—'
                        : Object.entries(row.revenue_check)
                            .map(([k, v]) => `${fmtFieldName(k)}: ${String(v)}`)
                            .join(' · ')}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FlagBadge flag={row.overall_flag} />
                        {row.needs_verification && (
                          <span className="text-[10px] font-semibold text-amber-700">
                            needs verification
                          </span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Discrepancies */}
          <div className="space-y-2">
            <p className="text-sm font-semibold">
              Discrepancies {result.discrepancies.length > 0 ? `(${result.discrepancies.length})` : ''}
            </p>
            {result.discrepancies.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No discrepancies detected across the years compared.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Year</TableHead>
                      <TableHead>Field</TableHead>
                      <TableHead>Tax Value</TableHead>
                      <TableHead>Bank Value</TableHead>
                      <TableHead>Variance</TableHead>
                      <TableHead>Status</TableHead>
                      {onAddDiscrepancyToMir && <TableHead>Action</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.discrepancies.map((d, idx) => (
                      <TableRow key={`${d.year}-${d.field}-${idx}`} className={FLAG_CELL_STYLES[d.flag] ?? ''}>
                        <TableCell className="font-medium">FY{d.year}</TableCell>
                        <TableCell>{fmtFieldName(d.field)}</TableCell>
                        <TableCell className="tabular-nums">{fmtUsd(d.tax_value)}</TableCell>
                        <TableCell className="tabular-nums">{fmtUsd(d.bank_value)}</TableCell>
                        <TableCell className="tabular-nums">
                          {fmtUsd(d.variance_abs)} ({fmtPct(d.variance_pct)})
                        </TableCell>
                        <TableCell>
                          <FlagBadge flag={d.flag} />
                        </TableCell>
                        {onAddDiscrepancyToMir && (
                          <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onAddDiscrepancyToMir(d)}
                            >
                              Add to MIR
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

function fmtFieldName(key: string): string {
  return key
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

export default CrossVerifyPanel
